import * as React from "react";
import { Grid, Message, Loader, Dimmer, Button } from "semantic-ui-react";
import { useIntl } from "react-intl";
import * as DTO from "../../../interfaces";
import { useAccountSettings, useSocialCoachSession } from "../../../utils";

import { DigestEmailSet } from "./DigestEmailSet";
import { DigestEmailType, descriptors } from "./descriptors";

import { DigestEmailContainer, DigestEmailGrid } from "./styled";
import "./styles.scss";
import { DigestEmailServices } from "../../../services";
import { WebComponents } from "../../../interfaces";

const DigestEmailFC: React.FC = () => {
  const { coachId } = useSocialCoachSession();
  const { formatMessage } = useIntl();
  const [loading, setLoading] = React.useState<boolean>(false);
  const [digestEmailList, setDigestEmailList] = React.useState<DTO.TagElement[]>([]);
  const [digestEmailListError, setDigestEmailListError] = React.useState<{ [key: string]: string } | undefined>();
  const { hasWritePermission } = useAccountSettings();
  const withWritePermission = hasWritePermission(WebComponents.DIGEST_EMAIL);
  const [generalErrors, setGeneralErrors] = React.useState<string[]>([]);

  React.useEffect(() => {
    const errorList: string[] = [];
    if (digestEmailListError) {
      Object.keys(digestEmailListError).map(key => {
        return digestEmailListError[key] && errorList.push(digestEmailListError[key]);
      });
    }
    setGeneralErrors(() => errorList);
  }, [digestEmailListError]);

  const setErrors = (errorKeywords: string[]): { [key: string]: string } | undefined => {
    let newErrors = {};
    errorKeywords.map((error: string, index: number) => {
      newErrors = {
        ...newErrors,
        [index]: error,
      };
    });

    return newErrors;
  };

  const onErrorDigestEmails = (errors: string[]): void => {
    setDigestEmailListError(() => setErrors(errors));
  };

  const updateDigestEmails = (): void => {
    const emails: DTO.DigestEmailElement[] = digestEmailList.map(digestEmail => {
      return {
        email: digestEmail.displayValue,
      } as DTO.DigestEmailElement;
    });

    if (emails.length > 100) {
      alert("Only 100 emails allowed");
      return;
    }

    setLoading(true);

    DigestEmailServices.update(coachId!.toString(), emails)
      .then(result => {
        setDigestEmailList(() => emailsInTagFormat(result));
      })
      .catch((e: string) => {
        console.error(e);
        alert(e.substring(0, 40));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const emailsInTagFormat = (result: DTO.DigestEmailElementResponse[]): DTO.TagElement[] => {
    return result.map((keyword, index) => {
      const k: DTO.TagElement = {
        index,
        displayValue: keyword.digestEmail,
        id: keyword.id,
      };
      return k;
    });
  };

  const getDigestEmails = async () => {
    const result = await DigestEmailServices.get(coachId!.toString());
    setDigestEmailList(emailsInTagFormat(result));
  };

  React.useEffect(() => {
    getDigestEmails().catch(console.error);
  }, [coachId]);

  return (
    <Grid className={"digestGrid"}>
      {generalErrors.length > 0 && (
        <Grid.Row columns={1}>
          <Message floating error list={generalErrors} />
        </Grid.Row>
      )}
      <DigestEmailGrid>
        <DigestEmailContainer columns={1}>
          {loading && (
            <Dimmer active inverted>
              <Loader inverted content="Loading" />
            </Dimmer>
          )}

          <Grid.Column width={16}>
            <DigestEmailSet
              title={formatMessage({
                ...descriptors[DigestEmailType.emailSectionTitle],
              })}
              emails={digestEmailList}
              onError={onErrorDigestEmails}
              customStyle={`background: rgba(61, 174, 245, 0.2);`}
              onSuccess={setDigestEmailList}
            />
          </Grid.Column>
        </DigestEmailContainer>
      </DigestEmailGrid>
      <Grid.Row>
        {withWritePermission && (
          <Grid.Row>
            <Button onClick={updateDigestEmails} primary loading={loading} className={"saveEmailsButton"}>
              Save
            </Button>
          </Grid.Row>
        )}
      </Grid.Row>
    </Grid>
  );
};

export const DigestEmails = DigestEmailFC;
